import React, { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { type ColumnDef } from '@tanstack/react-table'
import DataTable from '../../components/DataTable'
import Button from '../components/Button'
import { getGoalsByStudent } from '../../services/goals'
import { Goal, Student } from '../../types'
import { useStudentsStore } from '../../stores/studentsStore'
import Icon from '../components/Icon'

const StudentGoals: React.FC = () => {
  const { studentId } = useParams<{ studentId: string }>()
  const navigate = useNavigate()
  const { students } = useStudentsStore()
  const [goals, setGoals] = useState<Goal[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const student: Student | null = studentId
    ? students.find((s) => s._id === studentId) || null
    : null

  useEffect(() => {
    if (studentId) {
      fetchGoals()
    }
  }, [studentId])

  const fetchGoals = async () => {
    if (!studentId) return

    setLoading(true)
    setError(null)
    try {
      const goalsRes = await getGoalsByStudent(studentId)
      setGoals(goalsRes.data)
    } catch (err: any) {
      console.error('Error fetching goals:', err)
      setError(err.response?.data?.error || 'Failed to fetch goals')
    } finally {
      setLoading(false)
    }
  }

  const handleLogGoalEvent = (goalId: string) => {
    navigate(`/goals/${goalId}/goal-events`)
  }

  const columns = useMemo<ColumnDef<Goal>[]>(
    () => [
      {
        accessorKey: 'description',
        header: 'Description',
        cell: (info) => (
          <div className="text-sm text-gray-900">
            {info.getValue() as string}
          </div>
        ),
      },
      {
        accessorKey: 'category',
        header: 'Category',
        cell: (info) => (
          <div className="text-sm text-gray-900">
            {info.getValue() as string}
          </div>
        ),
      },
      {
        accessorKey: 'area',
        header: 'Area',
        cell: (info) => (
          <div className="text-sm text-gray-900">
            {info.getValue() as string}
          </div>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: (info) => (
          <Button
            onClick={(e) => {
              e.stopPropagation()
              handleLogGoalEvent(info.row.original._id)
            }}
            variant="secondary"
            className="ml-auto"
            title="View goal events"
          >
            <Icon type="eye" className="w-4 h-4 mr-1" />
            <span>View Progress Reports</span>
          </Button>
        ),
      },
    ],
    [navigate],
  )

  return (
    <div className="space-y-6">
      <div>
        <Button
          onClick={() => navigate('/goals/students')}
          variant="link"
          className="mb-4 flex items-center gap-2"
        >
          ← Back to Students
        </Button>
        <h1 className="text-3xl font-bold text-gray-900">
          {student
            ? `${student.firstName} ${student.lastName}'s Goals`
            : 'Student Goals'}
        </h1>
        {student && (
          <p className="mt-2 text-gray-600">
            School:{' '}
            {typeof student.school === 'string'
              ? student.school
              : student.school?.displayName}{' '}
            | Grade: {student.grade}
          </p>
        )}
      </div>

      <DataTable
        data={goals}
        columns={columns}
        searchPlaceholder="Search goals..."
        emptyStateTitle="No goals found"
        emptyStateDescription="This student doesn't have any goals yet."
        loading={loading}
      />

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}
    </div>
  )
}

export default StudentGoals
